import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { LocalStorageTourStorage } from '@digiwedge/tour-core';
import { useTour } from '@digiwedge/tour-react';
import { createSearchTourSteps } from './tour/searchTourSteps.js';

const TOUR_ID = 'teetime-onboarding-v1';
const STORAGE_KEY = 'teetime.tour.v1';
const TRIGGER_KEY = `${STORAGE_KEY}:trigger`;
const BLOCKED_PREFIXES = ['/login', '/signup', '/forgot-password', '/reset-password'];

export function TourBootstrapper(): null {
  const tour = useTour();
  const { pathname } = useLocation();
  const storageRef = useRef<LocalStorageTourStorage | null>(null);

  if (typeof window !== 'undefined' && !storageRef.current) {
    storageRef.current = new LocalStorageTourStorage();
  }

  useEffect(() => {
    const storage = storageRef.current;
    if (!storage || typeof window === 'undefined') return;

    const state = tour.getState();
    const blocked = BLOCKED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
    if (blocked) {
      if (state.active) tour.stop('skip');
      return;
    }

    const triggerSet = storage.get(TRIGGER_KEY) === '1';
    const completed = storage.get(`${STORAGE_KEY}:${TOUR_ID}:completed`) === '1';

    if (!triggerSet || completed) {
      if (state.active) tour.stop('skip');
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 10;

    const tryStart = () => {
      if (cancelled) return true;
      const steps = createSearchTourSteps();
      if (steps.length === 0) return false;

      tour.start(TOUR_ID, steps, {
        continuous: true,
        storageKey: STORAGE_KEY,
      });
      storage.set(TRIGGER_KEY, '0');
      return true;
    };

    if (state.active || tryStart()) {
      return;
    }

    const timer = window.setInterval(() => {
      attempts += 1;
      const started = tryStart();
      if (started || attempts >= maxAttempts) {
        window.clearInterval(timer);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [pathname, tour]);

  useEffect(() => {
    const storage = storageRef.current;
    if (!storage) return;

    const unsubscribe = tour.on((event) => {
      if (event.type === 'finish' || event.type === 'skip') {
        storage.set(TRIGGER_KEY, '0');
      }
    });

    return unsubscribe;
  }, [tour]);

  return null;
}

export default TourBootstrapper;
